import React, { useState, useEffect } from 'react';
import { FieldArray } from 'formik';
import { FaPlusCircle, FaTrashAlt } from 'react-icons/fa';
import {
  Box,
  Modal,
  Grid,
  MenuItem,
  Button,
  Typography,
  IconButton,
  Select,
  FormControl,
  InputLabel,
  Chip,
  OutlinedInput,
  Stack,
} from '@mui/material';
import { TextInput } from '../../layouts/FormElement';
import ShowFilesPopup from '../../halper/showFilesPopup';
import TextEditor from 'components/_dashboard/pod/capstones/TextEditor';

const NEW_SKILLS_LIST = [
  'Critical Thinking and Problem-Solving',
  'Communication Skills',
  'Collaboration and Teamwork',
  'Creativity and Innovation',
  'Self-Management and Accountability',
  'Leadership and Initiative',
  'Emotional Intelligence and Empathy',
  'Entrepreneurial Mindset',
];

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 1000,
  height: 600,
  bgcolor: 'background.paper',
  border: '2px solid #000',
  boxShadow: 24,
  p: 4,
};

const QuestionForm = ({
  values,
  setFieldValue,
  preSkills,
  questionPreValue,
  optionsPreValue,
}) => {
  const [open, setOpen] = useState(false);
  const handleClose = () => setOpen(false);
  const [selectedField, setSelectedField] = useState();
  const [refresh, setRefresh] = useState(0);
  const [selectedSkills, setSelectedSkills] = useState([]);

  // Initialize selected skills from existing option ratings
  useEffect(() => {
    if (values.options && values.options.length > 0) {
      const allSkills = new Set();
      values.options.forEach(option => {
        if (option.ratings && option.ratings.length > 0) {
          option.ratings
            .filter(rating => rating.skill && NEW_SKILLS_LIST.includes(rating.skill))
            .forEach(rating => allSkills.add(rating.skill));
        }
      });
      
      if (allSkills.size > 0) {
        setSelectedSkills(Array.from(allSkills));
      }
    }
  }, [values.options]);

  function setImage(valueToSetTo) {
    if (selectedField) {
      setFieldValue(selectedField, valueToSetTo);
    }
  }

  const handleSkillsChange = (event) => {
    const value = event.target.value;
    const skills = typeof value === 'string' ? value.split(',') : value;
    setSelectedSkills(skills);
  };

  return (
    <Grid>
      <Grid
        container
        rowSpacing={3}
        columnSpacing={{ xs: 2, sm: 1, md: 5 }}
        mb={3}>
        <Grid item xs={9}>
          <Typography variant='h6'>Question</Typography>
          <TextEditor
            setFieldValue={setFieldValue}
            initialtext={questionPreValue || ''}
            valueToUpdate='question'
          />
        </Grid>

        {/* Skills Selection at Question Level */}
        <Grid item xs={12}>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="skills-select-label">
              Select Skills to Evaluate
            </InputLabel>
            <Select
              labelId="skills-select-label"
              multiple
              value={selectedSkills}
              onChange={handleSkillsChange}
              input={<OutlinedInput label="Select Skills to Evaluate" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={value} size="small" />
                  ))}
                </Box>
              )}
            >
              {NEW_SKILLS_LIST.map((skill) => (
                <MenuItem key={skill} value={skill}>
                  {skill}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Maximum Select Options */}
        <Grid item xs={4}>
          <FormControl fullWidth>
            <InputLabel>Maximum options a user can select</InputLabel>
            <Select
              value={values.maxSelect || 1}
              label='Maximum options a user can select'
              onChange={(e) => {
                if (e.target.value > 1) {
                  const updatedOptions = Array.from(
                    { length: e.target.value },
                    (_, i) => i + 1
                  ).map((opt) => ({
                    correctAnswers: opt,
                    ratings: selectedSkills.map((skill) => ({
                      skill,
                      score: 0,
                    })),
                  }));
                  setFieldValue('multiSelectRatings', updatedOptions);
                }
                setFieldValue('maxSelect', e.target.value);
              }}
            >
              <MenuItem value={1}>1</MenuItem>
              <MenuItem value={2}>2</MenuItem>
              <MenuItem value={3}>3</MenuItem>
              <MenuItem value={4}>4</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <Grid item>
            {[...Array(1)].map((e, i) => (
              <Grid item key={i}>
                <FieldArray
                  name='image'
                  render={(arrayHelpers) => (
                    <>
                      <Grid
                        item
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '5px',
                        }}>
                        Add question image
                        <Button
                          type='button'
                          onClick={() => arrayHelpers.push()}>
                          <FaPlusCircle />
                        </Button>
                        {values.image.map((content, index) => {
                          return (
                            <Grid item key={index}>
                              <Grid
                                item
                                sm={12}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                }}>
                                <TextInput
                                  name={`image[${index}]`}
                                  type='text'
                                  placeholder='Image URL'
                                />
                                <Grid item>
                                  <Button
                                    type='button'
                                    onClick={() => {
                                      setOpen(true);
                                      setSelectedField(`image[${index}]`);
                                      setRefresh(!refresh);
                                    }}>
                                    Select File
                                  </Button>
                                </Grid>
                                <Grid item>
                                  <img
                                    src={values.image[index]}
                                    alt=''
                                    width='150'
                                  />
                                  <Grid item>
                                    <Button
                                      type='button'
                                      onClick={() =>
                                        arrayHelpers.remove(index)
                                      }>
                                      <FaTrashAlt />
                                    </Button>
                                  </Grid>
                                </Grid>
                              </Grid>
                            </Grid>
                          );
                        })}
                      </Grid>
                    </>
                  )}
                />
              </Grid>
            ))}
          </Grid>
        </Grid>
      </Grid>

      {(values.maxSelect || 1) > 1 ? (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FieldArray
              name='options'
              render={(arrayHelpers) => (
                <>
                  {values.options.map((option, index) => (
                    <Box key={index} sx={{ mb: 2 }}>
                      <Grid item xs={12}>
                        <Typography variant='h6'>
                          Option - {index + 1}{' '}
                        </Typography>
                        <TextEditor
                          setFieldValue={setFieldValue}
                          initialtext={
                            (optionsPreValue &&
                              optionsPreValue[index]?.option) ||
                            ''
                          }
                          valueToUpdate={`options[${index}].option`}
                        />
                      </Grid>
                      <FormControl sx={{ mt: 1, mr: 2 }}>
                        <InputLabel>Value</InputLabel>
                        <Select
                          value={option.value || 0}
                          label='Value'
                          onChange={(e) => setFieldValue(`options[${index}].value`, e.target.value)}
                        >
                          <MenuItem value={0}>0</MenuItem>
                          <MenuItem value={1}>1</MenuItem>
                        </Select>
                      </FormControl>
                      <IconButton
                        color='error'
                        onClick={() => arrayHelpers.remove(index)}>
                        <FaTrashAlt />
                      </IconButton>
                    </Box>
                  ))}
                  <Stack direction='row' spacing={2} alignItems='center' mt={2}>
                    <Button
                      variant='contained'
                      startIcon={<FaPlusCircle />}
                      onClick={() => {
                        arrayHelpers.push({
                          option: '',
                          value: 0,
                        });
                      }}>
                      Add Option
                    </Button>
                  </Stack>
                </>
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <FieldArray
              name='multiSelectRatings'
              render={(arrayHelpers) => (
                <>
                  {values.multiSelectRatings?.map((option, index) => (
                    <Box
                      key={index}
                      sx={{
                        border: '1px solid #ccc',
                        borderRadius: '8px',
                        padding: '16px',
                        marginTop: '16px',
                        backgroundColor: '',
                      }}>
                      <Grid container spacing={2} alignItems='center'>
                        <Grid item xs={12}>
                          <Typography variant='h6'>
                            Correct Answer: {option.correctAnswers}
                          </Typography>
                        </Grid>

                        <Grid item xs={12}>
                          <Grid container spacing={3}>
                            {option.ratings?.map((rating, ratingIndex) => (
                              <Grid
                                item
                                xs={12}
                                sm={6}
                                md={3}
                                key={ratingIndex}>
                                <Box>
                                  <Typography
                                    sx={{ mb: 1 }}
                                    variant='subtitle2'>
                                    {rating.skill}
                                  </Typography>
                                  <FormControl fullWidth>
                                    <InputLabel>Score</InputLabel>
                                    <Select
                                      value={rating.score}
                                      label='Score'
                                      onChange={(e) => setFieldValue(`multiSelectRatings[${index}].ratings[${ratingIndex}].score`, e.target.value)}
                                    >
                                      {[0, 0.25, 0.5, 0.75, 1].map((value) => (
                                        <MenuItem key={value} value={value}>
                                          {value}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                </Box>
                              </Grid>
                            ))}
                          </Grid>
                        </Grid>
                      </Grid>
                    </Box>
                  ))}
                </>
              )}
            />{' '}
          </Grid>
        </Grid>
      ) : (
        <Grid container spacing={1} direction='column'>
          <FieldArray
            name='options'
            render={(arrayHelpers) => (
              <>
                {values.options.map((option, index) => (
                  <Box
                    key={index}
                    sx={{
                      border: '1px solid #ccc',
                      borderRadius: '8px',
                      padding: '16px',
                      marginTop: '16px',
                      backgroundColor: '',
                    }}>
                    <Grid container spacing={2} alignItems='center'>
                      <Grid item xs={12}>
                        <Typography variant='h6'>
                          Option - {index + 1}{' '}
                        </Typography>
                        <TextEditor
                          setFieldValue={setFieldValue}
                          initialtext={
                            (optionsPreValue && optionsPreValue[index]?.option) ||
                            ''
                          }
                          valueToUpdate={`options[${index}].option`}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <Grid container spacing={3}>
                          {selectedSkills.map((skill, skillIndex) => {
                            const ratingIndex = option.ratings?.findIndex(r => r.skill === skill) ?? -1;
                            if (ratingIndex === -1) {
                              // Initialize rating if it doesn't exist
                              if (!option.ratings) {
                                setFieldValue(`options[${index}].ratings`, []);
                              }
                              setFieldValue(`options[${index}].ratings[${option.ratings?.length ?? 0}]`, {
                                skill,
                                score: 0
                              });
                            }
                            return (
                              <Grid item xs={12} sm={6} md={3} key={skillIndex}>
                                <Box>
                                  <Typography sx={{ mb: 1 }} variant='subtitle2'>
                                    {skill}
                                  </Typography>
                                  <FormControl fullWidth>
                                    <InputLabel>Score</InputLabel>
                                    <Select
                                      value={option.ratings?.[ratingIndex]?.score ?? 0}
                                      label='Score'
                                      onChange={(e) => setFieldValue(`options[${index}].ratings[${ratingIndex}].score`, e.target.value)}
                                    >
                                      {[0, 0.25, 0.5, 0.75, 1].map((value) => (
                                        <MenuItem key={value} value={value}>
                                          {value}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                </Box>
                              </Grid>
                            );
                          })}
                        </Grid>
                        {selectedSkills.length === 0 && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                            Please select skills above to see rating options
                          </Typography>
                        )}
                      </Grid>

                      <Grid item xs={12} sx={{ textAlign: 'right' }}>
                        <IconButton
                          color='error'
                          onClick={() => arrayHelpers.remove(index)}>
                          <FaTrashAlt />
                        </IconButton>
                      </Grid>
                    </Grid>
                  </Box>
                ))}
                <Button
                  type='button'
                  variant='outlined'
                  onClick={() =>
                    arrayHelpers.push({
                      option: '',
                      ratings: selectedSkills.map((skill) => ({
                        skill,
                        score: 0,
                      })),
                    })
                  }
                  sx={{ mt: 2 }}>
                  Add Option
                </Button>
              </>
            )}
          />
        </Grid>
      )}

      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby='modal-modal-title'
        aria-describedby='modal-modal-description'>
        <Box sx={style}>
          <ShowFilesPopup
            setImage={setImage}
            handleClose={handleClose}
            refresh={refresh}
          />
        </Box>
      </Modal>
    </Grid>
  );
};

export default QuestionForm;
